// ============================================
// MODALBORRARARCHIVADOS.JS — Borrado masivo permanente (pestaña Archivados)
// ============================================
// Borra PERMANENTEMENTE en lote registros archivados seleccionados.
// REUSA los endpoints individuales existentes (DELETE /:tipo/:id/permanente),
// que ya validan: estado archivado (409 NO_ARCHIVADO) e hijos colgando
// (409 NO_PUEDE_BORRAR con detalle) — la proteccion del CASCADE de
// clientes_sucursales.razon_social_id vive en el backend, no aqui.
// Ejecucion secuencial: una llamada por registro; si uno falla o se salta,
// los demas siguen. Confirmacion escribiendo el NUMERO (patron del proyecto).

import React, { useMemo, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloBotonPrimario, estiloBotonSecundario,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';

const ROJO = '#e05c5c';
const ROJO_BG = '#fde8e8';
const VERDE = '#1D9E75';
const AMBAR = '#b45309';
const AMBAR_BG = '#fef3c7';

const TIPO_META = {
  grupo:        { path: 'grupos',           label: 'grupo (cliente)',  plural: 'grupos (clientes)' },
  razon_social: { path: 'razones-sociales', label: 'razón social',     plural: 'razones sociales' },
  sucursal:     { path: 'sucursales',       label: 'sucursal',         plural: 'sucursales' },
  persona:      { path: 'personas',         label: 'persona',          plural: 'personas' },
};

const DETALLE_HUMANO = {
  razones_sociales:     (n) => `${n} razón(es) social(es)`,
  sucursales:           (n) => `${n} sucursal(es)`,
  sucursales_huerfanas: (n) => `${n} sucursal(es) huérfana(s)`,
  oportunidades:        (n) => `${n} expediente(s)`,
  roles_contacto:       (n) => `${n} rol(es) de contacto`,
};

function motivoDeBloqueo(detalle) {
  const partes = Object.entries(detalle || {})
    .filter(([k]) => DETALLE_HUMANO[k])
    .map(([k, n]) => DETALLE_HUMANO[k](n));
  return partes.length ? `tiene ${partes.join(' y ')} colgando` : 'tiene registros colgando';
}

// registros: [{ tipo, id, nombre }]
export default function ModalBorrarArchivados({ registros, onCerrar, onTerminado }) {
  const [filas, setFilas] = useState(() => (registros || []).map(r => ({
    ...r, estado: 'pendiente', mensaje: null, // pendiente|borrando|borrado|saltado|error
  })));
  const [confirmacion, setConfirmacion] = useState('');
  const [ejecutando, setEjecutando] = useState(false);
  const [terminado, setTerminado] = useState(false);

  const total = filas.length;
  const confirmada = String(total) === confirmacion.trim();

  // Resumen por tipo para la lista del modal.
  const resumen = useMemo(() => {
    const porTipo = {};
    filas.forEach(f => { porTipo[f.tipo] = (porTipo[f.tipo] || 0) + 1; });
    return Object.entries(porTipo)
      .map(([t, n]) => `${n} ${TIPO_META[t] ? TIPO_META[t].plural : t}`)
      .join(' · ');
  }, [filas]);

  const ejecutar = async () => {
    if (!confirmada || ejecutando) return;
    setEjecutando(true);
    for (let i = 0; i < filas.length; i++) {
      const f = filas[i];
      const meta = TIPO_META[f.tipo];
      if (!meta) {
        setFilas(prev => prev.map((x, k) => (k === i ? { ...x, estado: 'error', mensaje: 'tipo desconocido' } : x)));
        continue;
      }
      setFilas(prev => prev.map((x, k) => (k === i ? { ...x, estado: 'borrando' } : x)));
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch(`${API_BASE_CLI}/api/clientes/${meta.path}/${f.id}/permanente`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });
        // eslint-disable-next-line no-await-in-loop
        const d = await r.json().catch(() => ({}));
        if (r.ok && d.ok) {
          setFilas(prev => prev.map((x, k) => (k === i ? { ...x, estado: 'borrado' } : x)));
        } else if (r.status === 409 && d.error === 'NO_PUEDE_BORRAR') {
          setFilas(prev => prev.map((x, k) => (k === i
            ? { ...x, estado: 'saltado', mensaje: motivoDeBloqueo(d.detalle) } : x)));
        } else if (r.status === 409 && d.error === 'NO_ARCHIVADO') {
          setFilas(prev => prev.map((x, k) => (k === i
            ? { ...x, estado: 'saltado', mensaje: 'ya no está archivado' } : x)));
        } else if (r.status === 401 || r.status === 403) {
          setFilas(prev => prev.map((x, k) => (k === i
            ? { ...x, estado: 'error', mensaje: 'sin permisos (solo Dirección)' } : x)));
        } else {
          setFilas(prev => prev.map((x, k) => (k === i
            ? { ...x, estado: 'error', mensaje: d.mensaje || d.error || `error ${r.status}` } : x)));
        }
      } catch {
        setFilas(prev => prev.map((x, k) => (k === i ? { ...x, estado: 'error', mensaje: 'sin conexión' } : x)));
      }
    }
    setEjecutando(false);
    setTerminado(true);
  };

  const borrados = filas.filter(f => f.estado === 'borrado').length;
  const saltados = filas.filter(f => f.estado === 'saltado').length;
  const errores = filas.filter(f => f.estado === 'error').length;

  const chipEstado = (f) => {
    if (f.estado === 'borrado') return <span style={{ color: VERDE, fontSize: 11, fontWeight: 700 }}>BORRADO</span>;
    if (f.estado === 'borrando') return <span style={{ color: COLOR_GRIS_CLARO, fontSize: 11, fontWeight: 700 }}>BORRANDO…</span>;
    if (f.estado === 'saltado') return <span style={{ color: AMBAR, fontSize: 11, fontWeight: 700, background: AMBAR_BG, padding: '1px 6px', borderRadius: 99 }}>SALTADO</span>;
    if (f.estado === 'error') return <span style={{ color: ROJO, fontSize: 11, fontWeight: 700 }}>ERROR</span>;
    return null;
  };

  return (
    <div
      onClick={() => !ejecutando && onCerrar()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: 'min(94vw, 560px)',
          maxHeight: '90vh', overflowY: 'auto', padding: 20,
          fontFamily: PP, color: COLOR_GRIS, boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: ROJO }}>
          🗑 Borrar permanentemente {total} registro(s)
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS, margin: '6px 0 10px' }}>
          {resumen}
        </div>
        <div style={{
          background: ROJO_BG, color: '#8e3838', borderRadius: 10, padding: '10px 12px',
          fontSize: 12, fontWeight: 700, marginBottom: 12,
        }}>
          ⚠️ Esto NO tiene vuelta atrás. Los registros se eliminan de la base de datos para siempre.
          Los que tengan expedientes, sucursales o contactos colgando se SALTARÁN automáticamente.
        </div>

        <div style={{ display: 'grid', gap: 6, marginBottom: 14, maxHeight: '38vh', overflowY: 'auto' }}>
          {filas.map((f, i) => (
            <div key={`${f.tipo}-${f.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              padding: '6px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
              background: f.estado === 'borrado' ? '#f0fdf7' : (f.estado === 'error' ? ROJO_BG : '#fff'),
            }}>
              <span style={{ fontSize: 12, color: COLOR_OSCURO, fontWeight: 600, flex: '1 1 160px', minWidth: 0, wordBreak: 'break-word' }}>
                {f.nombre}
                <span style={{ color: COLOR_GRIS_CLARO, fontWeight: 400 }}> · {TIPO_META[f.tipo] ? TIPO_META[f.tipo].label : f.tipo}</span>
              </span>
              {chipEstado(f)}
              {f.mensaje && <span style={{ fontSize: 11, color: f.estado === 'saltado' ? AMBAR : ROJO, width: '100%' }}>{f.mensaje}</span>}
            </div>
          ))}
        </div>

        {!terminado && (
          <div style={{ background: COLOR_FONDO, borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 8 }}>
              Para confirmar el borrado DEFINITIVO, escribe el número <strong style={{ color: ROJO }}>{total}</strong>:
            </div>
            <input
              type="number"
              inputMode="numeric"
              value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)}
              placeholder={`Escribe ${total} para confirmar`}
              disabled={ejecutando}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 14px',
                fontSize: 16, fontFamily: PP, border: `1px solid ${COLOR_BORDE}`,
                borderRadius: 10, outline: 'none', color: COLOR_OSCURO,
              }}
            />
          </div>
        )}

        {(ejecutando || terminado) && (
          <div style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 12 }}>
            Progreso: {borrados} borrado(s)
            {saltados > 0 && ` · ${saltados} saltado(s)`}
            {errores > 0 && ` · ${errores} con error`}
            {` de ${total}`}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={onCerrar}
            disabled={ejecutando}
            onPointerDown={(e) => { e.currentTarget.style.opacity = '0.6'; }}
            onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
            onPointerLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            style={{ ...estiloBotonSecundario, minHeight: 44, transition: 'opacity 0.1s' }}
          >{terminado ? 'Cerrar' : 'Cancelar'}</button>
          {!terminado && (
            <button
              onClick={ejecutar}
              disabled={!confirmada || ejecutando}
              onPointerDown={(e) => { e.currentTarget.style.opacity = '0.6'; }}
              onPointerUp={(e) => { e.currentTarget.style.opacity = '1'; }}
              onPointerLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              style={{
                ...estiloBotonPrimario, minHeight: 44, background: ROJO,
                opacity: (!confirmada || ejecutando) ? 0.6 : 1,
                cursor: (!confirmada || ejecutando) ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.1s',
              }}
            >{ejecutando ? 'Borrando…' : `Borrar ${total} registro(s)`}</button>
          )}
          {terminado && borrados > 0 && (
            <button
              onClick={() => onTerminado && onTerminado()}
              style={{ ...estiloBotonPrimario, minHeight: 44 }}
            >Cerrar y actualizar lista</button>
          )}
        </div>
      </div>
    </div>
  );
}
